/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business QA責任評価詳細・確定ページ。バグ情報とQA責任判定を表示し、人が確定（Backlog書き戻し）する
 * @graph-connects useQaResponsibilityDetail [calls] QA責任評価詳細フック
 * @graph-connects useConfirmQaResponsibility [calls] QA責任確定フック
 */
import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useAuth } from '../../hooks/useAuth';
import {
  useQaResponsibilityDetail,
  useConfirmQaResponsibility,
} from '../../hooks/useQaResponsibility';

/** @graph-connects none */
export const Route = createFileRoute('/qa-responsibility/$issueKey')({
  component: QaResponsibilityDetailPage,
});

/** @graph-connects none */
const QA_OPTIONS = ['有', '無'] as const;

/** @graph-connects none */
function FieldBlock({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">{label}</div>
      <div className="whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">{value}</div>
    </div>
  );
}

/**
 * @graph-connects useQaResponsibilityDetail [calls] QA責任評価詳細フック
 * @graph-connects useConfirmQaResponsibility [calls] QA責任確定フック
 */
function QaResponsibilityDetailPage() {
  const { t, i18n } = useTranslation();
  const { issueKey } = Route.useParams();
  const { can } = useAuth();
  const canConfirm = can('CONFIRM_QA_RESPONSIBILITY');

  const { data, isLoading } = useQaResponsibilityDetail(issueKey);
  const detail = data?.data;
  const confirmMutation = useConfirmQaResponsibility(issueKey);

  const [qaResponsibility, setQaResponsibility] = useState<'有' | '無'>('無');
  const [reason, setReason] = useState<string>('');

  // 取得した値でフォームを初期化
  useEffect(() => {
    if (detail) {
      setQaResponsibility(detail.qaResponsibility === '有' ? '有' : '無');
      setReason(detail.qaResponsibilityReason);
    }
  }, [detail]);

  const locale = (i18n.language ?? 'ja').startsWith('ja') ? 'ja' : 'en';

  const handleConfirm = () => {
    confirmMutation.mutate({ qaResponsibility, qaResponsibilityReason: reason, locale });
  };

  if (isLoading) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="h-6 w-48 rounded bg-[var(--color-bg-elevated)] animate-pulse" />
        <Card className="p-4">
          <div className="h-32 w-full rounded bg-[var(--color-bg-elevated)] animate-pulse" />
        </Card>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="space-y-4">
        <Link to="/qa-responsibility" className="text-sm text-[var(--color-accent-primary)]">
          ← {t('qaResponsibility.backToList', '一覧へ戻る')}
        </Link>
        <Card className="p-8 text-center text-[var(--color-text-muted)]">
          {t('status.noData', 'データがありません')}
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <Link to="/qa-responsibility" className="text-sm text-[var(--color-accent-primary)]">
          ← {t('qaResponsibility.backToList', '一覧へ戻る')}
        </Link>
        {detail.isQaReviewed ? (
          <span className="rounded-full bg-green-100 px-3 py-1 text-xs text-green-700">
            {t('qaResponsibility.confirmedBy', '確定済み')}
            {detail.qaReviewedBy ? ` (${detail.qaReviewedBy})` : ''}
          </span>
        ) : (
          <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs text-yellow-700">
            {t('qaResponsibility.provisional', '仮（Gemini判定）')}
          </span>
        )}
      </div>

      <div>
        <h1 className="font-mono text-lg text-[var(--color-accent-primary)]">{detail.issueKey}</h1>
        <p className="mt-1 text-xl font-bold text-[var(--color-text-primary)]">
          {detail.issueSummary}
        </p>
        <p className="mt-1 text-xs text-[var(--color-text-muted)]">
          {detail.service} ・ {detail.bugType}
          {detail.causeProjectKey ? ` ・ ${detail.causeProjectKey}` : ''}
        </p>
      </div>

      {/* バグ詳細 */}
      <Card className="p-4 space-y-4">
        <h2 className="text-sm font-bold text-[var(--color-text-primary)]">
          {t('qaResponsibility.bugDetails', 'バグ詳細')}
        </h2>
        <FieldBlock label={t('qaResponsibility.bugSummary', '概要')} value={detail.bugSummary} />
        <FieldBlock
          label={t('qaResponsibility.expectedResult', '期待した結果')}
          value={detail.expectedResult}
        />
        <FieldBlock
          label={t('qaResponsibility.actualResult', '実際の結果')}
          value={detail.actualResult}
        />
        <FieldBlock label={t('qaResponsibility.bugReason', '原因')} value={detail.bugReason} />
        <FieldBlock
          label={t('qaResponsibility.stepsToReproduce', '再現手順')}
          value={detail.stepsToReproduce}
        />
      </Card>

      {/* QA責任の判定・確定 */}
      <Card className="p-4 space-y-4">
        <h2 className="text-sm font-bold text-[var(--color-text-primary)]">
          {t('qaResponsibility.judgment', 'QA責任の判定')}
        </h2>

        <div>
          <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
            {t('qaResponsibility.qaResponsibility', 'QA責任有無')}
          </label>
          <div className="flex gap-2">
            {QA_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                disabled={!canConfirm}
                aria-pressed={qaResponsibility === opt}
                onClick={() => {
                  setQaResponsibility(opt);
                }}
                className={`rounded-md border px-4 py-2 text-sm ${
                  qaResponsibility === opt
                    ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)] text-white'
                    : 'border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)]'
                } ${canConfirm ? '' : 'opacity-60 cursor-not-allowed'}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1">
            {t('qaResponsibility.reason', '判定理由')}
          </label>
          <textarea
            value={reason}
            disabled={!canConfirm}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            rows={5}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm disabled:opacity-60"
          />
        </div>

        {canConfirm ? (
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={confirmMutation.isPending}
              onClick={handleConfirm}
              className="rounded-md bg-[var(--color-accent-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-accent-hover)] disabled:opacity-60"
            >
              {confirmMutation.isPending
                ? t('qaResponsibility.confirming', '確定中...')
                : t('qaResponsibility.confirm', '確定する')}
            </button>
            {confirmMutation.isSuccess ? (
              <span className="text-xs text-[var(--color-success)]">
                {t('qaResponsibility.confirmed', '確定しました')}
                {confirmMutation.data?.backlogWriteBack
                  ? t('qaResponsibility.backlogSynced', '（Backlog反映済み）')
                  : t('qaResponsibility.backlogSkipped', '（Backlog反映なし）')}
              </span>
            ) : null}
            {confirmMutation.isError ? (
              <span className="text-xs text-[var(--color-error)]">
                {t('qaResponsibility.confirmError', '確定に失敗しました')}
              </span>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('qaResponsibility.noConfirmPermission', 'QA責任を確定する権限がありません')}
          </p>
        )}
      </Card>
    </div>
  );
}
